import Service, { service } from '@ember/service'
import { contentVariant } from 'teacher-ui-client/utils/content-variant'
import {
  ReadingEggsReadingProgressHandler,
  ReadingEggsSpellingProgressHandler,
  ReadingEggsStorylandsProgressHandler,
  ReadingEggspressReadingProgressHandler,
  ReadingEggspressSpellingProgressHandler,
  FastPhonicsPeaksProgressHandler,
  MathseedsLessonsProgressHandler,
  MathseedsMentalMinuteProgressHandler,
  MathseedsMentalMinuteCategory,
  MathseedsPrimeLessonsProgressHandler,
  WritingLegendsLessonsProgressHandler,
} from 'teacher-ui-client/progress-handler/utils'
import { MathseedsPrimeThread } from 'teacher-ui-client/progression-sequence/utils'
import type { IntlService } from 'ember-intl'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import type ProgressionSequenceService from 'teacher-ui-client/services/progression-sequence'
import type Features from 'teacher-ui-client/services/features'
import type SessionService from 'teacher-ui-client/services/session'

/**
 * This services creates the progress handlers and caches them.
 *
 * A progress handler getter should only be called after the lesson info it needs for its options is loaded.
 */
export default class ProgressHandlerService extends Service {
  @service intl!: IntlService

  @service lessonInfo!: LessonInfoService

  @service progressionSequence!: ProgressionSequenceService

  @service gradeSets!: GradeSetsService

  @service features!: Features

  @service session!: SessionService

  get curriculum() {
    return contentVariant(this.session.currentSchool.accent.extension)
  }

  get readingEggsReadingHandler() {
    return new ReadingEggsReadingProgressHandler(this.intl, this.lessonInfo, this.gradeSets)
  }

  get readingEggsSpellingHandler() {
    return new ReadingEggsSpellingProgressHandler(this.intl, this.lessonInfo, this.gradeSets)
  }

  get readingEggsStorylandsHandler() {
    return new ReadingEggsStorylandsProgressHandler(this.intl, this.lessonInfo, this.gradeSets)
  }

  get writingLegendsWritingHandler() {
    return new WritingLegendsLessonsProgressHandler(this.intl, this.lessonInfo)
  }

  get readingEggspressReadingHandler() {
    return new ReadingEggspressReadingProgressHandler(this.intl, this.lessonInfo, this.gradeSets)
  }

  get readingEggspressSpellingHandler() {
    return new ReadingEggspressSpellingProgressHandler(this.intl, this.lessonInfo, this.gradeSets)
  }

  get fastPhonicsPeaksHandler() {
    return new FastPhonicsPeaksProgressHandler(this.intl, this.lessonInfo)
  }

  get mathseedsLessonsHandler() {
    return new MathseedsLessonsProgressHandler(this.intl, this.lessonInfo, this.gradeSets)
  }

  get mathseedsMentalMinuteAddSubHandler() {
    return new MathseedsMentalMinuteProgressHandler(this.intl, this.lessonInfo, this.gradeSets, [
      MathseedsMentalMinuteCategory.Addition,
      MathseedsMentalMinuteCategory.Subtraction,
    ])
  }

  get mathseedsMentalMinuteMulDivHandler() {
    return new MathseedsMentalMinuteProgressHandler(this.intl, this.lessonInfo, this.gradeSets, [
      MathseedsMentalMinuteCategory.Multiplication,
      MathseedsMentalMinuteCategory.Division,
    ])
  }

  get disabledGradeNames() {
    return [
      this.features.isEnabled('tui_ms_prime_grade_three') ? '' : 'Year 3',
      this.features.isEnabled('tui_ms_prime_grade_five') ? '' : 'Year 5',
      this.features.isEnabled('tui_ms_prime_grade_six') ? '' : 'Year 6',
    ]
  }

  get mathseedsPrimeNumberHandler() {
    return new MathseedsPrimeLessonsProgressHandler(
      this.intl,
      this.progressionSequence,
      this.curriculum,
      MathseedsPrimeThread.Number,
      this.disabledGradeNames,
    )
  }

  get mathseedsPrimeFractionsHandler() {
    return new MathseedsPrimeLessonsProgressHandler(
      this.intl,
      this.progressionSequence,
      this.curriculum,
      MathseedsPrimeThread.Fractions,
      this.disabledGradeNames,
    )
  }

  get mathseedsPrimeOperationsHandler() {
    return new MathseedsPrimeLessonsProgressHandler(
      this.intl,
      this.progressionSequence,
      this.curriculum,
      MathseedsPrimeThread.Operations,
      this.disabledGradeNames,
    )
  }

  get mathseedsPrimeGeometryHandler() {
    return new MathseedsPrimeLessonsProgressHandler(
      this.intl,
      this.progressionSequence,
      this.curriculum,
      MathseedsPrimeThread.Geometry,
      this.disabledGradeNames,
    )
  }

  get mathseedsPrimeMeasurementHandler() {
    return new MathseedsPrimeLessonsProgressHandler(
      this.intl,
      this.progressionSequence,
      this.curriculum,
      MathseedsPrimeThread.Measurement,
      this.disabledGradeNames,
    )
  }
}

declare module '@ember/service' {
  interface Registry {
    'progress-handler': ProgressHandlerService
  }
}
